import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Chart, ArcElement, DoughnutController, Tooltip } from "chart.js";

Chart.register(ArcElement, DoughnutController, Tooltip);

const STATUS_CONFIG = [
  { key: "activeCount",     label: "Active",     lightColor: "#639922", darkColor: "#97C459" },
  { key: "endedCount",      label: "Ended",      lightColor: "#378ADD", darkColor: "#85B7EB" },
  { key: "supersededCount", label: "Superseded", lightColor: "#E24B4A", darkColor: "#F09595" },
];

const StatusBreakdown = ({ statusBreakdown, totalAllocations }) => {
  const theme     = useTheme();
  const t         = theme.custom;
  const isDark    = theme.palette.mode === "dark";
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  const colors = STATUS_CONFIG.map(s => isDark ? s.darkColor : s.lightColor);

  useEffect(() => {
    if (!statusBreakdown || !canvasRef.current) return;

    const values = STATUS_CONFIG.map(s => statusBreakdown[s.key] || 0);

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: STATUS_CONFIG.map(s => s.label),
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderColor: "transparent",
          borderWidth: 0,
          hoverOffset: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "72%",
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: t.surface2,
            borderColor: t.border2,
            borderWidth: 1,
            titleColor: t.text,
            bodyColor: t.text2,
            padding: 10,
            callbacks: {
              label: (ctx) => {
                const total = totalAllocations || 1;
                const pct   = ((ctx.parsed / total) * 100).toFixed(1);
                return `  ${ctx.parsed} allocations (${pct}%)`;
              },
            },
          },
        },
      },
    });

    return () => { chartRef.current?.destroy(); };
  }, [statusBreakdown, isDark]);

  if (!statusBreakdown) return null;

  const total = totalAllocations || 1;

  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: 12,
      overflow: "hidden",
    }}>

      {/* header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px",
        borderBottom: `1px solid ${t.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Status Breakdown</span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px",
          borderRadius: 20, background: t.accent2, color: t.accent,
        }}>
          {total} total
        </span>
      </div>

      {/* body */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 20,
        padding: "20px 18px",
      }}>

        {/* donut + center */}
        <div style={{ position: "relative", width: 120, height: 120, flexShrink: 0 }}>
          <canvas ref={canvasRef} />
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            pointerEvents: "none",
          }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: t.text, lineHeight: 1 }}>
              {total}
            </span>
            <span style={{ fontSize: 10, color: t.text3, marginTop: 3 }}>allocations</span>
          </div>
        </div>

        {/* rows */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
          {STATUS_CONFIG.map(({ key, label, lightColor, darkColor }, i) => {
            const count = statusBreakdown[key] || 0;
            const pct   = Math.round((count / total) * 100);
            const color = isDark ? darkColor : lightColor;
            return (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <div style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: color, flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 11, color: t.text2 }}>{label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: t.text }}>{count}</span>
                    <span style={{ fontSize: 10, color: t.text3 }}>{pct}%</span>
                  </div>
                </div>
                {/* progress bar */}
                <div style={{
                  height: 3, borderRadius: 2,
                  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                }}>
                  <div style={{
                    height: "100%", borderRadius: 2,
                    width: `${pct}%`,
                    background: color,
                    transition: "width .4s ease",
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatusBreakdown;